import { TablePagination } from "../../../components/ui/TablePagination";
import { FormSelect } from "../../../components/ui/select";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import i18n from "../../../i18n";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, ShieldAlert, Mail, Phone, KeyRound, Loader2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader, SearchInput, DataTable, LoadingState } from "../../../components/ui";
import { hrmsGet } from "../../../api/api";

function getTableColumns() {
  return [
    { label: i18n.t("common.employeeId"), key: "id" },
    { label: i18n.t("common.employeeName"), key: "name" },
    { label: i18n.t("common.email"), key: "email" },
    { label: i18n.t("common.phone"), key: "phone" },
    { label: i18n.t("status.status"), key: "status" },
    { label: i18n.t("common.actions"), key: "actions", className: "text-center w-28" }
  ];
}

export function EmployeeListPage() {
  const navigate = useNavigate();
  useTranslation();
  const tableColumns = useMemo(() => getTableColumns(), []);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [regeneratingPasswordFor, setRegeneratingPasswordFor] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordEmployee, setPasswordEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const openPasswordConfirmation = (empId, empName) => {
    setPasswordMessage(null);
    setShowPassword(false);
    setPasswordEmployee({ id: empId, name: empName });
  };


  const fetchCount = useCallback(async () => {
    try {
      const res = await hrmsGet("/emp-info/count", "HR01S");
      const unwrapped = res?.data ?? res;
      setTotalCount(Number(unwrapped) || 0);
    } catch (err) {
      console.error("Failed to fetch employee count:", err);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let path = "/emp-info/search";
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
        sortBy: "empId",
        sortDir: "ASC"
      });

      const query = debouncedSearchQuery.trim();
      if (query) {
        if (query.includes("@")) {
          path = "/emp-info/search/email";
          params.set("email", query);
          params.set("sortBy", "email");
        } else if (query.toLowerCase().startsWith("loc:") || query.toLowerCase().startsWith("location:")) {
          path = "/emp-info/search/location";
          const loc = query.replace(/^loc(ation)?:/i, "").trim();
          params.set("locationType", "work");
          params.set("location", loc);
          params.set("sortBy", "first_Name");
        } else {
          path = "/emp-info/search/name";
          params.set("searchTerm", query);
          params.set("sortBy", "first_Name");
        }
      }

      let res = await hrmsGet(`${path}?${params.toString()}`, "HR01S");
      let unwrapped = res?.data ?? res;
      let content = unwrapped?.content ?? (Array.isArray(unwrapped) ? unwrapped : []);

      // Fallback: If name search yields 0 results, try searching by location
      if (content.length === 0 && query && !query.includes("@") && !query.toLowerCase().startsWith("loc")) {
        try {
          const locParams = new URLSearchParams({
            page: String(page),
            size: String(size),
            locationType: "work",
            location: query,
            sortBy: "first_Name",
            sortDir: "ASC"
          });
          const locRes = await hrmsGet(`/emp-info/search/location?${locParams.toString()}`, "HR01S");
          const locUnwrapped = locRes?.data ?? locRes;
          const locContent = locUnwrapped?.content ?? (Array.isArray(locUnwrapped) ? locUnwrapped : []);
          if (locContent.length > 0) {
            content = locContent;
            unwrapped = locUnwrapped;
          }
        } catch (_) {
          // Ignore fallback errors and show original result
        }
      }

      setEmployees(content);
      setTotalPages(unwrapped?.totalPages ?? 1);
      setTotalElements(unwrapped?.totalElements ?? content.length);

      // Refresh count on data fetch
      fetchCount();
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      setError(i18n.t("onboarding.failedToLoadEmployeeRecords"));
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, debouncedSearchQuery, fetchCount]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const regeneratePassword = async () => {
    if (!passwordEmployee || regeneratingPasswordFor !== null) return;
    const empId = passwordEmployee.id;
    setRegeneratingPasswordFor(empId);
    setPasswordMessage(null);
    try {
      const response = await hrmsGet(`/emp-info/generate-pwd/${encodeURIComponent(empId)}?showPassword=${showPassword}`, "HR01U");
      const result = response?.data ?? response;
      setPasswordMessage({
        type: "success",
        text: showPassword && typeof result === "string" && result.trim()
          ? result
          : i18n.t("onboarding.messages.passwordRegeneratedSuccessfully")
      });
    } catch (err) {
      console.error("Failed to regenerate employee password:", err);
      setPasswordMessage({ type: "error", text: i18n.t("onboarding.messages.failedToRegeneratePassword") });
    } finally {
      setRegeneratingPasswordFor(null);
      setPasswordEmployee(null);
      setShowPassword(false);
    }
  };

  return (
    <AppLayout>
      <DialogPrimitive.Root
        open={passwordEmployee !== null}
        onOpenChange={(open) => {
          if (!open && regeneratingPasswordFor === null) {
            setPasswordEmployee(null);
            setShowPassword(false);
          }
        }}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-2xl bg-white p-6 shadow-xl">
            <DialogPrimitive.Title className="text-lg font-semibold">
              {i18n.t("onboarding.regeneratePassword")}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm per-file-hint">
              {i18n.t("onboarding.confirmPasswordRegeneration", { name: passwordEmployee?.name })}
            </DialogPrimitive.Description>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(event) => setShowPassword(event.target.checked)}
                disabled={regeneratingPasswordFor !== null}
                className="h-4 w-4 accent-primary" />
              {i18n.t("onboarding.showPassword")}
            </label>
            <div className="flex justify-end gap-2">
              <DialogPrimitive.Close asChild>
                <Button className="h-10 px-4" variant="outline" disabled={regeneratingPasswordFor !== null}>
                  {i18n.t("formActions.cancel")}
                </Button>
              </DialogPrimitive.Close>
              <Button className="h-10 px-4" onClick={regeneratePassword} disabled={regeneratingPasswordFor !== null}>
                {regeneratingPasswordFor !== null && <Loader2 className="h-4 w-4 animate-spin" />}
                {i18n.t("onboarding.regeneratePassword")}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <div className="space-y-6">
        <div>
          <PageHeader back={<PageBackLink to="/" />}
            title={i18n.t("onboarding.employeesWithCount", { count: totalCount })}
            action={
              <Button
                onClick={() => navigate("/hrms-team/onboarding/employees/onboard")}
                className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
                <UserPlus className="h-4 w-4" />
                <Trans i18nKey="formActions.addEmployee" />
              </Button>
            } />
        </div>
        <div className="mb-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setDebouncedSearchQuery(searchQuery);
              setPage(0);
            }}
            className="w-full sm:w-[280px]">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={i18n.t("formActions.searchByName2")}
              className="w-full max-w-full" />
          </form>
        </div>

        {passwordMessage &&
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${passwordMessage.type === "success" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`}>
            {passwordMessage.text}
          </div>}
        {loading ?
          <LoadingState message={i18n.t("onboarding.fetchingEmployeeRecords")} /> :
          error ?
            <div className="p-8 text-center text-sm text-error bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
              {error}
            </div> :
            employees.length > 0 ?
              <div className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
                <DataTable columns={tableColumns} minWidth="760px">
                  {employees.map((emp) => {
                    const empId = emp.EmpId ?? emp.empId ?? emp.employeeId ?? emp.id;
                    const empName = emp.First_name || emp.fullName || [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name || i18n.t("common.employee");
                    const empEmail = emp.email ?? emp.emailId ?? emp.email_Id ?? "—";
                    const empPhone = emp.Phone ?? emp.phone ?? "-";
                    const empStatus = emp.emp_status ?? "-";

                    return (
                      <tr
                        key={empId}
                        tabIndex={0}
                        onClick={() => navigate(`/hrms-team/onboarding/employees/onboard?edit=${encodeURIComponent(empId)}`)}
                        onKeyDown={(event) => {
                          if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            navigate(`/hrms-team/onboarding/employees/onboard?edit=${encodeURIComponent(empId)}`);
                          }
                        }}
                        title={i18n.t("formActions.editEmployee")} className="cursor-pointer hover:bg-workspace/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary transition-colors">
                        <td className="px-4 py-3.5 font-mono text-xs per-file-hint font-medium">
                          #{empId}
                        </td>
                        <td className="px-4 py-3.5 font-semibold text-foreground">
                          {empName}
                        </td>
                        <td className="px-4 py-3.5 per-file-hint text-sm">
                          {empEmail}
                        </td>
                        <td className="px-4 py-3.5 per-file-hint text-sm">
                          {empPhone}
                        </td>
                        <td className="px-4 py-3.5 per-file-hint text-sm">{empStatus}</td>
                        <td className="px-4 py-3.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openPasswordConfirmation(empId, empName);
                              }}
                              disabled={regeneratingPasswordFor !== null}
                              title={i18n.t("onboarding.regeneratePassword")}
                              aria-label={i18n.t("onboarding.regeneratePasswordFor", { name: empName })}
                              className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition disabled:cursor-not-allowed disabled:opacity-50">
                              {regeneratingPasswordFor === empId ?
                                <Loader2 className="h-4 w-4 animate-spin" /> :
                                <KeyRound className="h-4 w-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>);
                  })}
                </DataTable>

                <div className="md:hidden divide-y divide-(--color-text-border)/35">
                  {employees.map((emp) => {
                    const empId = emp.EmpId ?? emp.empId ?? emp.employeeId ?? emp.id;
                    const empName = emp.First_name || emp.fullName || [emp.firstName, emp.lastName].filter(Boolean).join(" ") || emp.name || i18n.t("common.employee");
                    const empEmail = emp.email ?? emp.emailId ?? emp.email_Id ?? "—";
                    const empPhone = emp.Phone ?? emp.phone ?? "-";
                    const empStatus = emp.emp_status ?? "-";

                    return (
                      <div
                        key={empId}
                        tabIndex={0}
                        onClick={() => navigate(`/hrms-team/onboarding/employees/onboard?edit=${encodeURIComponent(empId)}`)}
                        onKeyDown={(event) => {
                          if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault();
                            navigate(`/hrms-team/onboarding/employees/onboard?edit=${encodeURIComponent(empId)}`);
                          }
                        }}
                        title={i18n.t("formActions.editEmployee")} className="p-4 space-y-2 cursor-pointer hover:bg-workspace/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-sm text-foreground">{empName}</span>
                          <span className="text-[11px] font-mono per-file-hint">#{empId}</span>
                        </div>
                        <div className="text-xs per-file-hint space-y-1">
                          <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 shrink-0" /> {empEmail}</p>
                          <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0" /> {empPhone}</p>
                          <p>{i18n.t("status.status")}: {empStatus}</p>
                        </div>
                        <div className="pt-2 flex items-center justify-end gap-2 border-t border-(--color-text-border)/20">
                          <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                openPasswordConfirmation(empId, empName);
                              }}
                            disabled={regeneratingPasswordFor !== null}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition disabled:cursor-not-allowed disabled:opacity-50">
                            {regeneratingPasswordFor === empId ?
                              <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                              <KeyRound className="h-3.5 w-3.5" />}
                            <Trans i18nKey="onboarding.regeneratePassword" />
                          </button>
                        </div>
                      </div>);
                  })}
                </div>

                <div className="px-4 py-3 sm:px-6 border-t border-(--color-text-border)/50 bg-workspace/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="per-file-hint font-medium">
                      <Trans i18nKey="pagination.showing" /> <span className="font-semibold text-foreground">{totalElements === 0 ? 0 : page * size + 1}</span> <Trans i18nKey="common.to" />{" "}
                      <span className="font-semibold text-foreground">
                        {Math.min((page + 1) * size, totalElements)}
                      </span>{" "}
                      <Trans i18nKey="common.of" /> <span className="font-semibold text-foreground">{totalElements}</span> <Trans i18nKey="pagination.entries" />
                    </div>
                    <FormSelect
                      value={size}
                      onChange={(e) => {
                        setSize(Number(e.target.value));
                        setPage(0);
                      }}
                      className="h-8 rounded-lg border border-(--color-text-border) px-2 text-xs bg-white focus:outline-hidden">
                      <option value={10}><Trans i18nKey="serviceExplorer.10PerPage" /></option>
                      <option value={20}><Trans i18nKey="serviceExplorer.20PerPage" /></option>
                      <option value={50}><Trans i18nKey="serviceExplorer.50PerPage" /></option>
                    </FormSelect>
                  </div>

                  <TablePagination page={page + 1} totalPages={totalPages} totalItems={totalElements} pageSize={size} onPageChange={(next) => setPage(next - 1)}  />
                </div>
              </div> :

              <div className="text-center py-12 bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
                <ShieldAlert className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-medium per-file-hint"><Trans i18nKey="serviceExplorer.noEmployeesFoundMatchingTheSearchCriteria" /></p>
              </div>
        }
      </div>
    </AppLayout>);
}
